"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useAuth } from "@workos-inc/authkit-nextjs/components";
import { useLabsApiAuth } from "@/lib/labsIdentityContext";

const HIGHLIGHTS = [
  {
    title: "Run experiments",
    description: "We pilot new features and products with real fabrication workflows so we validate before we scale.",
  },
  {
    title: "Gather feedback",
    description: "Customers and partners enroll in experiments and share structured feedback so we build what matters.",
  },
  {
    title: "Prove new technologies",
    description: "From high-velocity automation to transformative capabilities, we identify and prove technologies for the Stratus ecosystem.",
  },
  {
    title: "Lead the industry",
    description: "Data-driven contracting and intelligent MEP fabrication solutions keep Stratus at the forefront of the industry.",
  },
] as const;

export default function HomeContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const fromLogin = searchParams.get("fromLogin") === "1";
  const { user, loading: authLoading, refreshAuth } = useAuth();
  const { profile, loading: identityLoading } = useLabsApiAuth();
  const [redirectTarget, setRedirectTarget] = useState<"/admin" | "/experiments" | "/pending-approval" | null>(null);

  const loading = authLoading || (user != null && identityLoading);

  useEffect(() => {
    if (loading || !user || !profile || !fromLogin) return;
    // Disabled users stay on the homepage (their only accessible page).
    if (profile.isDisabled) return;

    if (!profile.isAdmin && !profile.siteAccessApproved) {
      setRedirectTarget("/pending-approval");
    } else if (profile.isAdmin) {
      setRedirectTarget("/admin");
    } else {
      setRedirectTarget("/experiments");
    }
  }, [loading, user, profile, fromLogin]);

  useEffect(() => {
    if (redirectTarget) router.replace(redirectTarget);
  }, [redirectTarget, router]);

  const handleSignIn = () => {
    void refreshAuth({ ensureSignedIn: true });
  };

  if (loading) {
    return (
      <section className="flex min-h-[40vh] items-center justify-center">
        <p className="text-[var(--stratus-muted)]">Loading…</p>
      </section>
    );
  }

  if (user && fromLogin && redirectTarget) {
    return (
      <section className="flex min-h-[40vh] items-center justify-center">
        <p className="text-[var(--stratus-muted)]">Redirecting…</p>
      </section>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Hero – background is on page wrapper; gradient here for text readability */}
      <section
        className="relative flex flex-shrink-0 flex-col items-center justify-center px-4 py-12 text-center sm:px-6 md:py-16"
        style={{ minHeight: "42vh" }}
        aria-label="Hero"
      >
        <div
          className="absolute inset-0 z-0 bg-gradient-to-b from-black/50 via-black/45 to-black/55"
          aria-hidden
        />
        <div className="relative z-10">
          <p className="max-w-2xl text-2xl font-bold text-white/95 drop-shadow sm:text-3xl md:text-4xl">
            The innovation branch of Stratus
          </p>
          <p className="mt-4 max-w-xl text-xl text-white/85 drop-shadow sm:text-2xl">
            We run experiments and gather feedback from customers and partners to prove new technologies.
          </p>
        </div>
      </section>

      {/* What Stratus Labs does */}
      <section className="min-h-0 flex-shrink-0 border-t border-[var(--stratus-border)] bg-[var(--stratus-card-90)] py-8 sm:py-10">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <h2 className="text-center text-3xl font-bold tracking-tight text-[var(--foreground)] sm:text-4xl">
            What we do
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-center text-lg text-[var(--stratus-muted)]">
            Stratus Labs accelerates data-driven contracting by identifying and proving new technologies for the Stratus ecosystem.
          </p>
          <div className="mt-6 grid grid-cols-2 gap-5 sm:grid-cols-4 lg:gap-6">
            {HIGHLIGHTS.map((item, i) => (
              <div
                key={i}
                className="rounded-lg border border-[var(--stratus-border)] bg-[var(--background)] p-5 shadow-sm transition hover:border-[var(--stratus-primary)]/30"
              >
                <h3 className="text-lg font-semibold text-[var(--foreground)]">
                  {item.title}
                </h3>
                <p className="mt-2 text-base leading-snug text-[var(--stratus-muted)]">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA: fills remaining space, content centered */}
      <section className="flex min-h-0 flex-1 flex-col items-center justify-center border-t border-[var(--stratus-border)] bg-[var(--background)] py-8">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6">
          <h2 className="text-center text-3xl font-bold tracking-tight text-[var(--foreground)] sm:text-4xl">
            Get started
          </h2>
          {user && profile?.isDisabled ? (
            <p className="mx-auto mt-3 max-w-2xl text-center text-lg text-[var(--stratus-muted)]">
              Your account is disabled. Select your name in the top-right corner to
              reactivate it and regain access to experiments.
            </p>
          ) : user ? (
            <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
              <Link
                href="/experiments/"
                className="inline-flex rounded-lg bg-[var(--stratus-primary)] px-6 py-3 text-base font-semibold text-white shadow-sm hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--stratus-primary)] focus:ring-offset-2"
              >
                View experiments
              </Link>
              {profile?.isAdmin && (
                <Link
                  href="/admin/"
                  className="inline-flex rounded-lg border-2 border-[var(--stratus-border)] bg-[var(--stratus-card)] px-6 py-3 text-base font-semibold text-[var(--foreground)] hover:bg-[var(--stratus-border)]/20 focus:outline-none focus:ring-2 focus:ring-[var(--stratus-muted)] focus:ring-offset-2"
                >
                  Admin
                </Link>
              )}
            </div>
          ) : (
            <>
              <p className="mx-auto mt-3 max-w-2xl text-center text-lg text-[var(--stratus-muted)]">
                Sign in with your Stratus account to see experiments, enroll, and share feedback.
              </p>
              <button
                type="button"
                onClick={handleSignIn}
                className="mt-6 inline-flex rounded-lg bg-[var(--stratus-primary)] px-6 py-3 text-base font-semibold text-white shadow-sm hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--stratus-primary)] focus:ring-offset-2"
              >
                Sign in to get started
              </button>
            </>
          )}
          <p className="mx-auto mt-6 max-w-2xl text-sm text-[var(--stratus-muted)]">
            Experiments are experimental software.{" "}
            <Link href="/experimental-software-notice/" className="text-[var(--stratus-primary)] hover:underline">
              Read the experimental software notice
            </Link>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
